<template>
  <v-dialog
    v-model="dialog"
    max-width="560"
    persistent
  >
    <v-card>
      <v-toolbar
        dark
        flat
        color="secondary"
      >
        <v-btn
          icon
          dark
          @click="close"
        >
          <v-icon>close</v-icon>
        </v-btn>
        <v-toolbar-title>בדיקה - {{ partnerName }}</v-toolbar-title>
      </v-toolbar>

      <v-card-text>
        <div class="mb-3 grey--text text--darken-1">
          בדיקה אחרונה:
          <strong>{{ lastCheckLabel }}</strong>
        </div>

        <v-textarea
          v-model="message"
          label="טקסט לבדיקה"
          outline
          auto-grow
          rows="5"
        />

        <div
          v-if="partnerLanguages.length"
          class="mb-3"
        >
          <div class="caption grey--text mb-1">תרגום לפי שפת הספק</div>
          <v-btn
            v-for="lang in partnerLanguages"
            :key="'lang' + lang._id"
            small
            class="ml-1 mb-1"
            :color="activeLangId === lang._id ? 'secondary' : undefined"
            :dark="activeLangId === lang._id"
            :loading="translatingLangId === lang._id"
            :disabled="translating"
            @click="translateToLanguage(lang)"
          >
            {{ lang.title || lang.code || 'שפה' }}
          </v-btn>
          <v-btn
            v-if="originalMessage"
            small
            outline
            class="ml-1 mb-1"
            :disabled="translating"
            @click="restoreOriginalMessage"
          >
            מקורי
          </v-btn>
        </div>

        <div
          v-if="!phones.length && !emails.length"
          class="mb-2 red--text"
        >
          לא נמצאו פרטי טלפון או אימייל לספק זה
        </div>

        <div
          v-if="phones.length"
          class="mb-2"
        >
          <div class="caption grey--text mb-1">טלפונים לשליחה ב-WhatsApp</div>
          <v-btn
            v-for="phone in phones"
            :key="'p' + phone"
            small
            class="ml-1 mb-1"
            :color="selectedPhone === phone ? 'green' : undefined"
            :dark="selectedPhone === phone"
            @click="selectPhone(phone)"
          >
            {{ phone }}
          </v-btn>
        </div>

        <div
          v-if="emails.length"
          class="mb-2"
        >
          <div class="caption grey--text mb-1">אימיילים לשליחה</div>
          <v-btn
            v-for="email in emails"
            :key="'e' + email"
            small
            class="ml-1 mb-1"
            :color="selectedEmail === email ? 'blue' : undefined"
            :dark="selectedEmail === email"
            @click="selectEmail(email)"
          >
            {{ email }}
          </v-btn>
        </div>
       
      </v-card-text>

      <v-card-actions class="pa-3">
        <v-spacer />
        <v-btn
          flat
          @click="close"
        >
          ביטול
        </v-btn>
        <v-btn
          color="green darken-1"
          dark
          :disabled="!canSendWhatsapp"
          :loading="saving"
          @click="sendWhatsapp"
        >
          <v-icon left>chat</v-icon>
          WhatsApp
        </v-btn>
        <v-btn
          color="blue darken-1"
          dark
          :disabled="!canSendEmail"
          :loading="saving"
          @click="sendEmail"
        >
          <v-icon left>email</v-icon>
          אימייל
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { differenceInCalendarDays } from "date-fns";
import { getApi } from "@/api";

export default {
  props: {
    value: {
      type: Boolean,
      default: false
    },
    partner: {
      type: Object,
      default: null
    },
    eventId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      message: "",
      originalMessage: "",
      selectedPhone: null,
      selectedEmail: null,
      saving: false,
      loadingPartner: false,
      translating: false,
      translatingLangId: null,
      activeLangId: null
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      }
    },
    partnerName() {
      return (this.partner && this.partner.name) || "ספק";
    },
    lastCheckLabel() {
      if (!this.partner || !this.partner.lastValidation) {
        return "אף פעם";
      }
      const days = differenceInCalendarDays(
        new Date(),
        new Date(this.partner.lastValidation)
      );
      if (isNaN(days)) return "אף פעם";
      if (days <= 0) return "היום";
      return `לפני ${days} ימים`;
    },
    phones() {
      return this.collectPhones();
    },
    emails() {
      return this.collectEmails();
    },
    partnerLanguages() {
      return this.collectPartnerLanguages();
    },
    canSendWhatsapp() {
      return !!this.selectedPhone && !!this.message.trim();
    },
    canSendEmail() {
      return !!this.selectedEmail && !!this.message.trim();
    }
  },
  watch: {
    value: {
      handler(isOpen) {
        if (isOpen && this.partner) {
          this.initDialog();
        }
      },
      immediate: true
    }
  },
  methods: {
    close() {
      this.dialog = false;
    },
    async initDialog() {
      this.selectedPhone = null;
      this.selectedEmail = null;
      this.activeLangId = null;
      this.translatingLangId = null;
      await this.ensurePartnerContacts();
      this.message = this.buildDefaultMessage();
      this.originalMessage = this.message;
      this.applyDefaultSelections();
    },
    applyDefaultSelections() {
      if (this.phones.length && !this.selectedPhone) {
        this.selectedPhone = this.phones[0];
      }
      if (this.emails.length && !this.selectedEmail) {
        this.selectedEmail = this.emails[0];
      }
    },
    selectPhone(phone) {
      this.selectedPhone = phone;
    },
    selectEmail(email) {
      this.selectedEmail = email;
    },
    buildDefaultMessage() {
      const contactEntries = this.collectContactEntries();
      const contactsBlock = this.formatContactEntriesBlock(contactEntries);
      const reviewSection = contactsBlock
        ? `Please review your details below:\n${contactsBlock}\n`
        : "";
      return `Hello,
This is Rescue One (International Search & Rescue). We are currently updating our emergency contact list.
${reviewSection}⚠️ If any of this information is outdated or incorrect, please reply with the updates so we can fix our records. (If everything is 100% correct, a quick 👍 is perfect).
If you have any questions or need a quick refresh on our previous coordination, just reply and let us know!
Thank you for helping us stay ready,
Rescue One Operations`;
    },
    collectContactEntries() {
      const partner = this.getPartnerEntity();
      if (!partner) return [];

      const entries = [];
      (partner.contacts || []).forEach(contactId => {
        const contact =
          typeof contactId === "string"
            ? this.$store.state.entities.contacts[contactId]
            : contactId;
        if (!contact) return;

        const phones = [];
        const emails = [];
        const seenPhones = {};
        const seenEmails = {};

        if (contact.phone) {
          this.addPhoneValue(contact.phone, phones, seenPhones);
        }
        (contact.contactDetails || []).forEach(detailId => {
          const detail =
            typeof detailId === "string"
              ? this.$store.state.entities.contactDetails[detailId]
              : detailId;
          if (!detail) return;
          if (this.isPhoneDetail(detail)) {
            this.addPhoneValue(detail.data, phones, seenPhones);
          } else if (this.isEmailDetail(detail)) {
            this.addEmailValue(detail.data, emails, seenEmails);
          }
        });

        if (contact.name || phones.length || emails.length) {
          entries.push({ name: contact.name || "", phones, emails });
        }
      });

      const partnerPhones = [];
      const partnerEmails = [];
      const seenPartnerPhones = {};
      const seenPartnerEmails = {};
      (partner.contactDetails || []).forEach(detailId => {
        const detail =
          typeof detailId === "string"
            ? this.$store.state.entities.contactDetails[detailId]
            : detailId;
        if (!detail) return;
        if (this.isPhoneDetail(detail)) {
          this.addPhoneValue(detail.data, partnerPhones, seenPartnerPhones);
        } else if (this.isEmailDetail(detail)) {
          this.addEmailValue(detail.data, partnerEmails, seenPartnerEmails);
        }
      });
      if (partnerPhones.length || partnerEmails.length) {
        entries.push({
          name: "",
          phones: partnerPhones,
          emails: partnerEmails
        });
      }

      return entries;
    },
    addPhoneValue(value, phones, seen) {
      const display = String(value || "").trim();
      const key = this.normalizePhone(display);
      if (!key || seen[key]) return;
      seen[key] = true;
      phones.push(display);
    },
    addEmailValue(value, emails, seen) {
      const display = String(value || "").trim();
      const key = display.toLowerCase();
      if (!key || seen[key]) return;
      seen[key] = true;
      emails.push(display);
    },
    formatContactEntriesBlock(entries) {
      if (!entries.length) return "";

      return entries
        .map(entry => {
          return [
            `    • Name: ${entry.name || "—"}`,
            `    • Phone: ${entry.phones.length ? entry.phones.join(", ") : "—"}`,
            `    • Email: ${entry.emails.length ? entry.emails.join(", ") : "—"}`
          ].join("\n");
        })
        .join("\n\n");
    },
    restoreOriginalMessage() {
      this.message = this.originalMessage;
      this.activeLangId = null;
    },
    collectPartnerLanguages() {
      const partner = this.getPartnerEntity();
      if (!partner) return [];

      const languages = [];
      const seen = {};
      const pushLang = langIdOrObj => {
        if (!langIdOrObj) return;
        const lang =
          typeof langIdOrObj === "string"
            ? this.$store.state.entities.languages[langIdOrObj]
            : langIdOrObj;
        if (!lang || !lang._id || seen[lang._id]) return;
        if (!lang.code && !lang.title) return;
        seen[lang._id] = true;
        languages.push(lang);
      };

      (partner.contacts || []).forEach(contactId => {
        const contact =
          typeof contactId === "string"
            ? this.$store.state.entities.contacts[contactId]
            : contactId;
        if (!contact || !contact.languages) return;
        contact.languages.forEach(pushLang);
      });

      // Fallback: if contacts have no languages, show active system languages
      if (!languages.length) {
        const all = this.$store.getters["entityList"]("languages") || [];
        all.forEach(pushLang);
      }

      return languages;
    },
    resolveLangCode(lang) {
      if (!lang) return null;
      if (lang.code) {
        return String(lang.code).trim().toLowerCase().split(/[-_]/)[0];
      }
      const title = String(lang.title || "").trim().toLowerCase();
      const map = {
        עברית: "he",
        hebrew: "he",
        אנגלית: "en",
        english: "en",
        רוסית: "ru",
        russian: "ru",
        ארמנית: "hy",
        armenian: "hy",
        ערבית: "ar",
        arabic: "ar",
        צרפתית: "fr",
        french: "fr",
        ספרדית: "es",
        spanish: "es",
        גרמנית: "de",
        german: "de",
        איטלקית: "it",
        italian: "it",
        פורטוגזית: "pt",
        portuguese: "pt",
        טורקית: "tr",
        turkish: "tr",
        יוונית: "el",
        greek: "el",
        סינית: "zh",
        chinese: "zh",
        יפנית: "ja",
        japanese: "ja"
      };
      return map[title] || null;
    },
    async translateToLanguage(lang) {
      if (!lang || this.translating) return;
      const targetLang = this.resolveLangCode(lang);
      if (!targetLang) {
        this.$store.dispatch("SHOW_SNACKBAR", {
          text: "לא נמצא קוד שפה לתרגום (" + (lang.title || "") + ")",
          color: "error"
        });
        return;
      }

      const textToTranslate = this.message;
      if (!textToTranslate.trim()) return;

      this.translating = true;
      this.translatingLangId = lang._id;
      try {
        const api = await getApi();
        const { data } = await api.post("/translate", {
          text: textToTranslate,
          targetLang,
          sourceLang: "auto"
        });
        if (data && data.translatedText) {
          this.message = data.translatedText;
          this.activeLangId = lang._id;
        }
      } catch (err) {
        this.$store.dispatch("SHOW_SNACKBAR", {
          text: err.response ? err.response.data.message : err.message,
          color: "error"
        });
      } finally {
        this.translating = false;
        this.translatingLangId = null;
      }
    },
    async ensurePartnerContacts() {
      if (!this.partner || !this.partner._id) return;
      this.loadingPartner = true;
      try {
        await this.$store.dispatch("API_GET", {
          url: "/partners/" + this.partner._id,
          entityType: "partners",
          schema: "partnerListSchema"
        });
      } catch (err) {
        console.error(err);
      } finally {
        this.loadingPartner = false;
      }
    },
    getPartnerEntity() {
      if (!this.partner) return null;
      return (
        this.$store.state.entities.partners[this.partner._id] || this.partner
      );
    },
    getContactDetails() {
      const partner = this.getPartnerEntity();
      if (!partner) return [];

      const details = [];
      const detailIds = partner.contactDetails || [];
      detailIds.forEach(id => {
        const detail =
          typeof id === "string"
            ? this.$store.state.entities.contactDetails[id]
            : id;
        if (detail) details.push(detail);
      });

      const contactIds = partner.contacts || [];
      contactIds.forEach(contactId => {
        const contact =
          typeof contactId === "string"
            ? this.$store.state.entities.contacts[contactId]
            : contactId;
        if (!contact) return;
        if (contact.phone) {
          details.push({ data: contact.phone, type: null, _phoneHint: true });
        }
        (contact.contactDetails || []).forEach(detailId => {
          const detail =
            typeof detailId === "string"
              ? this.$store.state.entities.contactDetails[detailId]
              : detailId;
          if (detail) details.push(detail);
        });
      });

      return details;
    },
    isPhoneDetail(detail) {
      if (!detail || !detail.data) return false;
      if (detail._phoneHint) return true;
      const type = this.$store.state.entities.contactDetailTypes[detail.type];
      if (type && type.formatAsPhoneNumber) return true;
      const cleaned = String(detail.data).replace(/[^\d+]/g, "");
      return cleaned.length >= 8 && !String(detail.data).includes("@");
    },
    isEmailDetail(detail) {
      if (!detail || !detail.data) return false;
      const type = this.$store.state.entities.contactDetailTypes[detail.type];
      const title = ((type && type.title) || "").toLowerCase();
      if (title.includes("מייל") || title.includes("email") || title.includes("mail")) {
        return true;
      }
      return /.+@.+\..+/.test(String(detail.data));
    },
    normalizePhone(phone) {
      return String(phone || "").replace(/[^\d]/g, "");
    },
    collectPhones() {
      const phones = [];
      this.getContactDetails().forEach(detail => {
        if (!this.isPhoneDetail(detail)) return;
        const phone = this.normalizePhone(detail.data);
        if (phone && phones.indexOf(phone) === -1) {
          phones.push(phone);
        }
      });
      return phones;
    },
    collectEmails() {
      const emails = [];
      this.getContactDetails().forEach(detail => {
        if (!this.isEmailDetail(detail)) return;
        const email = String(detail.data).trim().toLowerCase();
        if (email && emails.indexOf(email) === -1) {
          emails.push(email);
        }
      });
      return emails;
    },
    async sendWhatsapp() {
      if (!this.canSendWhatsapp) return;
      const phone = this.selectedPhone;
      const email = this.selectedEmail || "";
      this.saving = true;
      try {
        const api = await getApi();
        const response = await api.post(`/partners/${this.partner._id}/send-check-whatsapp`, {
          phone,
          email
        });
        await this.$store.dispatch("API_GET", {
          url: "/partners/" + this.partner._id,
          entityType: "partners",
          schema: "partnerListSchema"
        });
        this.$emit("checked", this.partner._id);
        const isDryRun = response.data.whatsapp && response.data.whatsapp.dryRun;
        this.$store.dispatch("SHOW_SNACKBAR", {
          text: isDryRun
            ? "WhatsApp dry-run (מקומי) — ההודעה לא נשלחה בפועל. בפרודקשן/Inforu IP whitelist תקבל שליחה אמיתית"
            : "הודעת WhatsApp נשלחה בהצלחה",
          color: isDryRun ? "warning" : "success"
        });
        this.close();
      } catch (err) {
        this.$store.dispatch("SHOW_SNACKBAR", {
          text: err.response ? err.response.data.message : err.message,
          color: "error"
        });
      } finally {
        this.saving = false;
      }
    },
    async sendEmail() {
      if (!this.canSendEmail) return;
      const to = this.selectedEmail;
      const message = this.message;
      this.saving = true;
      try {
        const api = await getApi();
        await api.post(`/partners/${this.partner._id}/send-check-email`, {
          to,
          subject: "Test email - Global Rescue",
          message
        });
        await this.$store.dispatch("API_GET", {
          url: "/partners/" + this.partner._id,
          entityType: "partners",
          schema: "partnerListSchema"
        });
        this.$emit("checked", this.partner._id);
        this.$store.dispatch("SHOW_SNACKBAR", {
          text: "האימייל נשלח בהצלחה",
          color: "success"
        });
        this.close();
      } catch (err) {
        this.$store.dispatch("SHOW_SNACKBAR", {
          text: err.response ? err.response.data.message : err.message,
          color: "error"
        });
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/events/PartnerCheckDialog.vue